import React, { useEffect, useState, useCallback } from 'react';
import './PeriodicReportsPage.scss';
import Loader from '../../shared/components/common/loader/Loader';
import { PeriodicReportService, PeriodicReportResponse } from '../../shared/services/integrations/PeriodicReportService';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog, ConfirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import PeriodicReportCreate from './components/create/PeriodicReportCreate';
import PeriodicReportLink from './components/link/PeriodicReportLink';
import { format } from 'date-fns';
import { getProjectsFromAccount } from '../../shared/helpers/project/ProjectHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Title from '../../shared/components/title/Title';
import MoreBtn from '../../shared/components/common/more-btn/MoreBtn';
import { LookerStudioService } from '../../shared/services/integrations/LookerStudioService';
import EmptyState from '../../shared/components/common/empty-state/EmptyState';
import { getStatusName, getStatusClass, getProviderName, getTypeName } from '../../shared/helpers/periodic-reports/PeriodicReportsHelper';

const PeriodicReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const [reports, setReports] = useState<PeriodicReportResponse[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{[key: number]: boolean}>({});
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  
  // State for report link modal
  const [isSavingUrl, setIsSavingUrl] = useState(false);
  
  let confirmRef: ConfirmDialogReturn;

  // Available providers for filtering
  const providerOptions = [
    { label: t('allProviders'), value: null },
    { label: t('lookerStudio'), value: 'LOOKER_STUDIO' },
  ];

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await PeriodicReportService.getPeriodicReports(undefined, selectedProvider || undefined);
      setReports(data);
      setError(null);
    } catch (err) {
      setError(t('failedToLoadPeriodicReports'));
    } finally {
      setLoading(false);
    }
  }, [selectedProvider, t]);

  // Read provider from URL on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const providerParam = searchParams.get('provider');
    const providerValue = providerParam ? providerParam : null;
    setSelectedProvider(providerValue);
  }, [location.search]);

  // Load reports when component mounts or provider changes
  useEffect(() => {
    loadReports();
  }, [selectedProvider, loadReports]);

  const handleProviderChange = (e: any) => {
    const newProvider = e.value;
    setSelectedProvider(newProvider);
    
    // Update URL
    const searchParams = new URLSearchParams(location.search);
    if (newProvider !== null) {
      searchParams.set('provider', newProvider.toString());
    } else {
      searchParams.delete('provider');
    }
    
    const newUrl = `${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    navigate(newUrl, { replace: true });
  };

  const hideDialog = () => {
    if (confirmRef) {
      confirmRef.hide();
    }
    loadReports();
  };

  const handleReportCreated = async (reportId: number) => {
    try {
      // Close the create dialog first
      if (confirmRef) {
        confirmRef.hide();
      }
      await loadReports();
      const report = await PeriodicReportService.getPeriodicReport(reportId);
      if (report) {
        openReportLinkDialog(report);
      }
    } catch (error) {
      setError(t('failedToLoadReportConfiguration'));
    }
  };

  const openCreateReportDialog = () => {
    confirmRef = confirmDialog({
      message: <PeriodicReportCreate afterSave={hideDialog} onReportCreated={handleReportCreated} />,
      header: t('createPeriodicReport'),
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  const openReportLinkDialog = (report: PeriodicReportResponse) => {
    // Note: URL state will be managed by the PeriodicReportLink component itself
    
    // Create functions that use the report parameter directly instead of state
    const handleOpenCreateReportLink = () => {
      if (report) {
        const resolved = LookerStudioService.getReportUrl(report.id);
        resolved.then(result => {
          if (result?.url) {
            window.open(result.url, '_blank', 'noopener,noreferrer');
          }
        }).catch(error => {
          setError(t('failedToOpenReport'));
        });
      }
    };

    const handleSaveFinalReportUrl = async (url: string) => {
      if (!report || !url) {
        return;
      }
      
      if (!url.includes('lookerstudio.google.com')) {
        setError(t('invalidLookerStudioUrl'));
        return;
      }
      
      setIsSavingUrl(true);
      try {
        const success = await LookerStudioService.saveFinalReportUrl(report.id, url);
        if (success) {
          hideDialog();
        } else {
          setError(t('failedToSaveReportUrl'));
        }
      } catch (error) {
        setError(t('failedToSaveReportUrl'));
      } finally {
        setIsSavingUrl(false);
      }
    };
    
    confirmRef = confirmDialog({
      message: (
        <PeriodicReportLink
          currentReport={report}
          isSavingUrl={isSavingUrl}
          onOpenCreateReportLink={handleOpenCreateReportLink}
          onSaveFinalReportUrl={handleSaveFinalReportUrl}
        />
      ),
      header: t('configureReport'),
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  const confirmDelete = (report: PeriodicReportResponse) => {
    confirmDialog({
      message: t('confirmDeletePeriodicReport'),
      header: t('confirmDeletion'),
      defaultFocus: 'accept',
      accept: () => deleteReport(report),
    });
  };

  const confirmToggleStatus = (report: PeriodicReportResponse) => {
    const action = report.status === 'ACTIVE' ? t('deactivate') : t('activate');
    confirmDialog({
      message: `${t('areYouSureYouWantTo')} ${action} ${t('thisPeriodicReport')}?`,
      header: `${t('confirm')} ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      defaultFocus: 'accept',
      accept: () => toggleReportStatus(report),
    });
  };

  const deleteReport = async (report: PeriodicReportResponse) => {
    setActionLoading(prev => ({ ...prev, [report.id]: true }));
    try {
      const success = await PeriodicReportService.deletePeriodicReport(report.id);
              if (success) {
          await loadReports(); // Refresh the list
        } else {
          setError(t('failedToDeleteReport'));
        }
    } catch (err) {
      setError(t('failedToDeleteReportTryAgain'));
    } finally {
      setActionLoading(prev => ({ ...prev, [report.id]: false }));
    }
  };

  const toggleReportStatus = async (report: PeriodicReportResponse) => {
    setActionLoading(prev => ({ ...prev, [report.id]: true }));
    try {
      if (report.status === 'ACTIVE') {
        await PeriodicReportService.deactivatePeriodicReport(report.id);
      } else {
        await PeriodicReportService.activatePeriodicReport(report.id);
      }
      await loadReports(); // Refresh the list
    } catch (err) {
      setError(t('failedToUpdateReportStatus'));
    } finally {
      setActionLoading(prev => ({ ...prev, [report.id]: false }));
    }
  };

  const getProjectName = (projectId: number) => {
    const filteredProjects = getProjectsFromAccount(accountResponse, projectResponse || []);
    const project = filteredProjects.find((p: any) => p.id === projectId);
    return project ? project.projectLabel : `Project ${projectId}`;
  };



  const getReportName = (report: PeriodicReportResponse) => {
    if (report.config?.parameters?.reportName) {
      return report.config.parameters.reportName;
    }
    return `${getProjectName(report.projectId)} - ${getTypeName(report.type as string)} ${t('report')}`;
  };

  const tryOpenReport = async (reportId: number): Promise<{ success: boolean; url?: string; needsModal: boolean }> => {
    try {
      const resolved = await LookerStudioService.getReportUrl(reportId);
      
      if (resolved?.url) {
        if (resolved.status === 'exists') {
          return { success: true, url: resolved.url, needsModal: false };
        } else if (resolved.status === 'create') {
          return { success: true, needsModal: true };
        }
      }
      
      return { success: false, needsModal: false };
    } catch (error) {
      return { success: false, needsModal: false };
    }
  };

  const showReportModal = (report: PeriodicReportResponse) => {
    openReportLinkDialog(report);
  };

  const openReportInNewWindow = async (report: PeriodicReportResponse) => {
    try {
      // First, check if report is already active based on local data
      if (report.status === 'ACTIVE' && report.config?.parameters?.finalReportUrl) {
        // Report is active and has final URL - open directly
        window.open(report.config.parameters.finalReportUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      // If not active locally, try to get current status from backend
      const result = await tryOpenReport(report.id);
      
      if (result.success) {
        if (result.url) {
          // Report exists - open directly
          window.open(result.url, '_blank', 'noopener,noreferrer');
          // Refresh table to show updated status
          await loadReports();
          return;
        } else if (result.needsModal) {
          // Need to create report - show modal
          showReportModal(report);
          return;
        }
      }

      // If backend check failed, refresh data and check status again
      await loadReports();
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const updatedReport = reports?.find(r => r.id === report.id);
      if (!updatedReport) {
        setError(t('reportNotFound'));
        return;
      }

      // Check if status changed to PENDING or if we need to show modal
      if (updatedReport.status === 'PENDING') {
        showReportModal(updatedReport);
      } else {
        // Try one more time with updated data
        const finalResult = await tryOpenReport(updatedReport.id);
        
        if (finalResult.success && finalResult.url) {
          window.open(finalResult.url, '_blank', 'noopener,noreferrer');
          await loadReports();
        } else {
          showReportModal(updatedReport);
        }
      }
    } catch (error) {
      // Fallback to stored URL if available
      const reportUrl = report.config?.parameters?.finalReportUrl || report.config?.parameters?.reportUrl;
      if (reportUrl) {
        window.open(reportUrl, '_blank', 'noopener,noreferrer');
      } else {
        setError(t('failedToOpenReport'));
      }
    }
  };

  const actionBodyTemplate = (report: PeriodicReportResponse) => {
    const isLoading = actionLoading[report.id];
    
    return (
      <div className="buttons-list">
        {/* Toggle Status Button */}
        <Button
          className="action-btn"
          onClick={() => confirmToggleStatus(report)}
          disabled={isLoading}
          title={report.status === 'ACTIVE' ? t('deactivate') : t('activate')}
        >
          {isLoading ? (
            <span className="loading-dots">...</span>
          ) : (
            <span className={`ico--${report.status === 'ACTIVE' ? 'pause' : 'run'} ico--xs`} />
          )}
        </Button>
        
        {/* Delete Button */}
        <Button
          className="action-btn"
          onClick={() => confirmDelete(report)}
          disabled={isLoading}
          title={t('delete')}
        >
          {isLoading ? (
            <span className="loading-dots">...</span>
          ) : (
            <span className="ico--trash ico--xs" />
          )}
        </Button>
      </div>
    );
  };

  const providerFilterAndButtons = () => {
    return (
      <>
        <div className="page-box__buttons">
          <div className="form__row form__row--inline provider-filter">
            <div className="form__label">
              {t('selectProvider')}:
            </div>
            <Dropdown
              id="provider-filter"
              value={selectedProvider}
              options={providerOptions}
              onChange={handleProviderChange}
              placeholder={t('allProviders')}
              className="dropdown"
              optionLabel="label"
              optionValue="value"
            />
          </div>
          <button 
            className="btn btn--sm"
            onClick={() => openCreateReportDialog()}
          >
            {t('createReport')}
          </button>
        </div>
      </>
    );
  };

  const getCreateReportButton = () => {
    return (
      <button 
        className="btn btn--sm"
        onClick={() => openCreateReportDialog()}
      >
        {t('createReport')}
      </button>
    );
  };

  const emptyStateData = {
    title: t('noPeriodicReportsYet'),
    imageClassName: 'img--project',
    children: getCreateReportButton(),
  };

  if (loading) {
    return (
      <div className="periodic-reports-page">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="periodic-reports-page">
        <div className="error-message">
          <p>{error}</p>
          <Button label={t('retry')} onClick={loadReports} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-box">
        <Title title={t('periodicReports')} children={providerFilterAndButtons()} />
        
        <div className="page-box__content">
          <div className="periodic-reports-page">
            {reports && reports.length > 0 && (
              <div className="periodic-reports-page__description">
                <p>{t('manageAutomatedReports')}</p>
              </div>
            )}

            {error && (
              <div className="error-banner">
                <p>{error}</p>
                <Button 
                  label="×" 
                  className="error-banner__close"
                  onClick={() => setError(null)}
                />
              </div>
            )}

            {reports && reports.length > 0 ? (
              <div className="periodic-reports-page__table">
                <DataTable
                  stripedRows
                  paginator
                  paginatorClassName="pagination"
                  removableSort
                  sortField="created"
                  sortOrder={-1}
                  rows={50}
                  rowsPerPageOptions={[10, 25, 50]}
                  value={reports}
                  tableStyle={{ minWidth: '50rem' }}
                  tableClassName="table"
                  className="table-wrapper"
                  currentPageReportTemplate="Rows:"
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                >
          <Column
            field="id"
            header={t('reportName')}
            sortable
            headerClassName="table__header"
            body={(report) => (
              <div className="table__cell--with-icon">
                <span className="cell-text">
                  {getReportName(report)}
                </span>
                {report.provider === 'LOOKER_STUDIO' && (
                  <span
                    className="cell-icon"
                    onClick={() => openReportInNewWindow(report)}
                    title={report.status === 'PENDING' && report.provider === 'LOOKER_STUDIO' ? t('configureReport') : t('openReportInLookerStudio')}
                  >
                    <span className="ico--eye ico--xs" />
                  </span>
                )}
              </div>
            )}
            style={{ minWidth: '180px' }}
          />
          <Column
            field="projectId"
            header={t('project')}
            sortable
            headerClassName="table__header"
            body={(report) => getProjectName(report.projectId)}
            style={{ minWidth: '120px' }}
          />
          <Column
            field="provider"
            header={t('provider')}
            sortable
            headerClassName="table__header"
            body={(report) => getProviderName(report.provider)}
            style={{ minWidth: '100px' }}
          />
          <Column
            field="type"
            header={t('type')}
            sortable
            headerClassName="table__header"
            body={(report) => getTypeName(report.type)}
            style={{ minWidth: '80px' }}
          />

          <Column
            field="status"
            header={t('status')}
            sortable
            headerClassName="table__header"
            body={(report) => (
              <span className={getStatusClass(report.status)}>
                {getStatusName(report.status, report.provider)}
              </span>
            )}
            style={{ minWidth: '80px' }}
          />
          <Column
            field="lastExecuted"
            header={t('lastExecuted')}
            sortable
            headerClassName="table__header"
            body={(report) => report.lastExecuted ? format(new Date(report.lastExecuted), 'MMM d, HH:mm') : t('never')}
            style={{ minWidth: '120px' }}
          />
          <Column
            field="created"
            header={t('created')}
            sortable
            headerClassName="table__header"
            body={(report) => format(new Date(report.created), 'MMM d, yyyy')}
            style={{ minWidth: '100px' }}
          />
          <Column
            header={t('actions')}
            body={(report) => (
              <MoreBtn children={actionBodyTemplate(report)} rowId={report.id} />
            )}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
          />
                </DataTable>
              </div>
            ) : (
              <div className="empty-state-page">
                <EmptyState emptyStateData={emptyStateData} />
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog className="dialog-box" />
    </div>
  );
};

export default PeriodicReportsPage;
